import request from '@utils/request';
import { normalize } from 'normalizr';
import { Dispatch } from 'redux';
import { FormSubmissionError } from '@store/errors';
import type { AxiosResponse } from 'axios';
import { entitiesStructureMap, entitiesStructureMapList, EntityName } from '../entities-map';
import { FetchQueryOptionsType, NormalizedStore } from '../entity.types';

const PREFIX = '[ENTITIES]';

export const FETCH_ENTITY_REQUEST = `${PREFIX} FETCH_ENTITY_REQUEST`;
export const FETCH_ENTITY_SUCCESS = `${PREFIX} FETCH_ENTITY_SUCCESS`;
export const FETCH_ENTITY_FAILURE = `${PREFIX} FETCH_ENTITY_FAILURE`;

export const fetchEntityRequest = (entityName) => ({
  type: FETCH_ENTITY_REQUEST,
  entityName,
});

export const fetchEntitySuccess = (entityName, { data = [], isOverwrite }) => {
  const normalizedData = normalize(data, entitiesStructureMapList[entityName]);
  return {
    type: FETCH_ENTITY_SUCCESS,
    entityName,
    payload: {
      ...(data.length ? normalizedData : { entities: { [entityName]: [] } }),
      result: {
        [entityName]: normalizedData.result,
      },
      isOverwrite,
    },
  };
};

export const fetchEntityFailure = (entityName) => ({
  type: FETCH_ENTITY_FAILURE,
  entityName,
});

export const fetchEntity =
  (entityName, { url, params = {} }) =>
  (dispatch: Dispatch) => {
    dispatch(fetchEntityRequest(entityName));
    return request.get(url, { params: { ...params } }).then(
      ({ data }) => dispatch(fetchEntitySuccess(entityName, data)),
      () => {
        dispatch(fetchEntityFailure(entityName));
      }
    );
  };

export const FETCH_PAGINATED_ENTITY_REQUEST = `${PREFIX} FETCH_PAGINATED_ENTITY_REQUEST`;
export const FETCH_PAGINATED_ENTITY_SUCCESS = `${PREFIX} FETCH_PAGINATED_ENTITY_SUCCESS`;
export const FETCH_PAGINATED_ENTITY_FAILURE = `${PREFIX} FETCH_PAGINATED_ENTITY_FAILURE`;

export const fetchPaginatedEntityRequest = (entityName) => ({
  type: FETCH_PAGINATED_ENTITY_REQUEST,
  entityName,
});

export const fetchPaginatedEntitySuccess = (entityName, { data, ...pagination }) => {
  const normalizedData = normalize(data, entitiesStructureMapList[entityName]);
  return {
    type: FETCH_PAGINATED_ENTITY_SUCCESS,
    entityName,
    payload: {
      data: {
        ...normalizedData,
        result: {
          [entityName]: normalizedData.result,
        },
      },
      pagination: {
        [entityName]: pagination,
      },
    },
  };
};

export const fetchPaginatedEntityFailure = (entityName) => ({
  type: FETCH_PAGINATED_ENTITY_FAILURE,
  entityName,
});

export const fetchPaginatedEntity =
  (entityName, { url, params = {}, isOptional = true }) =>
  (dispatch: Dispatch) => {
    dispatch(fetchPaginatedEntityRequest(entityName));
    return request.get(url, { params: { ...params } }).then(
      ({ data }) => dispatch(fetchPaginatedEntitySuccess(entityName, data)),
      (error) => {
        dispatch(fetchPaginatedEntityFailure(entityName));
        if (!isOptional) {
          throw error;
        }
      }
    );
  };

export const FETCH_FILTERED_ENTITY_REQUEST = `${PREFIX} FETCH_FILTERED_ENTITY_REQUEST`;
export const FETCH_FILTERED_ENTITY_SUCCESS = `${PREFIX} FETCH_FILTERED_ENTITY_SUCCESS`;
export const FETCH_FILTERED_ENTITY_FAILURE = `${PREFIX} FETCH_FILTERED_ENTITY_FAILURE`;

export const fetchFilteredEntityRequest = (entityName) => ({
  type: FETCH_FILTERED_ENTITY_REQUEST,
  entityName,
});

export const fetchFilteredEntitySuccess = (entityName, filters, { data, ...pagination }) => {
  const normalizedData = normalize(data, entitiesStructureMapList[entityName]);
  return {
    type: FETCH_FILTERED_ENTITY_SUCCESS,
    entityName,
    payload: {
      data: {
        ...normalizedData,
        result: {
          [entityName]: normalizedData.result,
        },
      },
      filters,
      pagination: {
        [entityName]: pagination,
      },
    },
  };
};

export const fetchFilteredEntityFailure = (entityName, filters) => ({
  type: FETCH_FILTERED_ENTITY_FAILURE,
  entityName,
  payload: {
    filters,
  },
});

export const fetchFilteredEntity =
  (entityName, filters, { params, url }) =>
  (dispatch: Dispatch) => {
    dispatch(fetchFilteredEntityRequest(entityName));

    return request.get(url, { params: { ...filters, ...params, fallback: 1 } }).then(
      ({ data }) => dispatch(fetchFilteredEntitySuccess(entityName, filters, data)),
      () => {
        dispatch(fetchFilteredEntityFailure(entityName, filters));
      }
    );
  };

export const FETCH_ENTITY_SINGLE_REQUEST = `${PREFIX} FETCH_ENTITY_SINGLE_REQUEST`;
export const FETCH_ENTITY_SINGLE_SUCCESS = `${PREFIX} FETCH_ENTITY_SINGLE_SUCCESS`;
export const FETCH_ENTITY_SINGLE_FAILURE = `${PREFIX} FETCH_ENTITY_SINGLE_FAILURE`;

export const fetchEntitySingleRequest = (entityName) => ({
  type: FETCH_ENTITY_SINGLE_REQUEST,
  entityName,
});

export const fetchEntitySingleSuccess = (entityName, data) => {
  const normalizedData = normalize(data, entitiesStructureMap[entityName]);

  return {
    type: FETCH_ENTITY_SINGLE_SUCCESS,
    entityName,
    payload: {
      ...normalizedData,
      result: {
        [entityName]: normalizedData.result,
      },
    },
  };
};

export const fetchEntitySingleFailure = (entityName) => ({
  type: FETCH_ENTITY_SINGLE_FAILURE,
  entityName,
});

export const fetchEntitySingle =
  (entityName, { params = {}, url, isOptional = true }) =>
  (dispatch: Dispatch) => {
    dispatch(fetchEntitySingleRequest(entityName));
    return request.get(url, { params: { ...params, fallback: 1 } }).then(
      ({ data }) => {
        dispatch(fetchEntitySingleSuccess(entityName, data));
      },
      (error) => {
        dispatch(fetchEntitySingleFailure(entityName));
        if (!isOptional) {
          throw error;
        }
      }
    );
  };

export const UPDATE_ENTITY_SINGLE_REQUEST = `${PREFIX} UPDATE_ENTITY_SINGLE_REQUEST`;
export const UPDATE_ENTITY_SINGLE_SUCCESS = `${PREFIX} UPDATE_ENTITY_SINGLE_SUCCESS`;
export const UPDATE_ENTITY_SINGLE_FAILURE = `${PREFIX} UPDATE_ENTITY_SINGLE_FAILURE`;

export const updateEntitySingleRequest = (entityName) => ({
  type: UPDATE_ENTITY_SINGLE_REQUEST,
  entityName,
});

export const updateEntitySingleSuccess = (entityName, data) => {
  const normalizedData = normalize(data, entitiesStructureMap[entityName]);
  return {
    type: UPDATE_ENTITY_SINGLE_SUCCESS,
    entityName,
    payload: {
      ...normalizedData,
      result: {
        [entityName]: normalizedData.result,
      },
    },
  };
};

export const updateEntitySingleFailure = (entityName) => ({
  type: UPDATE_ENTITY_SINGLE_FAILURE,
  entityName,
});

export const updateEntitySingle =
  (entityName, { url, data }) =>
  (dispatch: Dispatch) => {
    dispatch(updateEntitySingleRequest(entityName));
    return request.patch(url, data).then(
      ({ data }) => dispatch(updateEntitySingleSuccess(entityName, data)),
      (error) => {
        dispatch(updateEntitySingleFailure(entityName));
        if (error.response && error.response.data) {
          throw new FormSubmissionError(error.response.data);
        }
      }
    );
  };

export const CREATE_ENTITY_SINGLE_REQUEST = `${PREFIX} CREATE_ENTITY_SINGLE_REQUEST`;
export const CREATE_ENTITY_SINGLE_SUCCESS = `${PREFIX} CREATE_ENTITY_SINGLE_SUCCESS`;
export const CREATE_ENTITY_SINGLE_FAILURE = `${PREFIX} CREATE_ENTITY_SINGLE_FAILURE`;

export const createEntitySingleRequest = (entityName) => ({
  type: CREATE_ENTITY_SINGLE_REQUEST,
  entityName,
});

export const createEntitySingleSuccess = (entityName, data) => {
  const normalizedData = normalize(data, entitiesStructureMap[entityName]);
  return {
    type: CREATE_ENTITY_SINGLE_SUCCESS,
    entityName,
    payload: {
      ...normalizedData,
      result: {
        [entityName]: normalizedData.result,
      },
    },
  };
};

export const createEntitySingleFailure = (entityName) => ({
  type: CREATE_ENTITY_SINGLE_FAILURE,
  entityName,
});

export const createEntitySingle =
  (entityName, { url, data }) =>
  (dispatch: Dispatch) => {
    dispatch(createEntitySingleRequest(entityName));
    return request.post(url, data).then(
      ({ data }) => dispatch(createEntitySingleSuccess(entityName, data)),
      (error) => {
        dispatch(createEntitySingleFailure(entityName));
        if (error.response && error.response.data) {
          throw new FormSubmissionError(error.response.data);
        }
      }
    );
  };

export const DELETE_ENTITY_SINGLE_REQUEST = `${PREFIX} DELETE_ENTITY_SINGLE_REQUEST`;
export const DELETE_ENTITY_SINGLE_SUCCESS = `${PREFIX} DELETE_ENTITY_SINGLE_SUCCESS`;
export const DELETE_ENTITY_SINGLE_FAILURE = `${PREFIX} DELETE_ENTITY_SINGLE_FAILURE`;

export const deleteEntitySingleRequest = (entityName) => ({
  type: DELETE_ENTITY_SINGLE_REQUEST,
  entityName,
});

export const deleteEntitySingleSuccess = (entityName, entityId) => ({
  type: DELETE_ENTITY_SINGLE_SUCCESS,
  entityName,
  payload: {
    [entityName]: {
      id: entityId,
    },
  },
});

export const deleteEntitySingleFailure = (entityName, error) => ({
  type: DELETE_ENTITY_SINGLE_FAILURE,
  entityName,
  payload: {
    error,
  },
});

export const deleteEntitySingle =
  (entityName, { url, id }) =>
  (dispatch: Dispatch) => {
    dispatch(deleteEntitySingleRequest(entityName));
    return request.delete(url).then(
      () => dispatch(deleteEntitySingleSuccess(entityName, id)),
      (error) => {
        dispatch(deleteEntitySingleFailure(entityName, error));
      }
    );
  };

export const FETCH_PARTIAL_ENTITY_REQUEST = `${PREFIX} FETCH_PARTIAL_ENTITY_REQUEST`;
export const FETCH_PARTIAL_ENTITY_SUCCESS = `${PREFIX} FETCH_PARTIAL_ENTITY_SUCCESS`;
export const FETCH_PARTIAL_ENTITY_FAILURE = `${PREFIX} FETCH_PARTIAL_ENTITY_FAILURE`;

export const fetchPartialEntityRequest = (entityName: EntityName) => ({
  type: FETCH_PARTIAL_ENTITY_REQUEST,
  entityName,
});

export const fetchPartialEntitySuccess = <T>(entityName: EntityName, data: T) => {
  const normalizedData = normalize(data, entitiesStructureMap[entityName]);
  return {
    type: FETCH_PARTIAL_ENTITY_SUCCESS,
    entityName,
    payload: {
      ...normalizedData,
      result: {
        [entityName]: normalizedData.result,
      },
    },
  };
};

export const fetchPartialEntityFailure = (entityName) => ({
  type: FETCH_PARTIAL_ENTITY_FAILURE,
  entityName,
});

/**
 * @description
 * Fetch part of an entity, ie. item, and replace it on entity with received data
 * <E, I> -> EntityType, ItemType
 * @param entityName entity name that holds the partial data we're interested in
 * @param entityId entity id that we should query
 * @param itemName item name that we are fetching; it must be a child of entity
 * @param customizer? function that will shape the partial data before inserting in store
 * @returns function
 */
export function fetchPartialEntity<E, I>(
  entityName: EntityName,
  entityId: number | string,
  itemName: string,
  { url, params = {}, isOptional = true }: FetchQueryOptionsType,
  customizer?: (data: E, orig_items: I, response_items: I) => E
): (dispatch: Dispatch, getState: () => NormalizedStore) => Promise<void> {
  return (dispatch, getState) => {
    dispatch(fetchPartialEntityRequest(entityName));
    return request.get(url, { params }).then(
      (response: AxiosResponse<I>) => {
        const { [itemName]: items, ...rest } = getState().entities[entityName].byId[entityId];
        const updatedData = customizer
          ? customizer(rest, items, response.data)
          : { ...rest, [itemName]: response.data };
        dispatch(fetchPartialEntitySuccess<E>(entityName, updatedData));
      },
      (error) => {
        dispatch(fetchPartialEntityFailure(entityName));
        if (!isOptional) {
          throw error;
        }
      }
    );
  };
}

export const FETCH_ENTITY_PER_DAY_REQUEST = `${PREFIX} FETCH_ENTITY_PER_DAY_REQUEST`;
export const FETCH_ENTITY_PER_DAY_SUCCESS = `${PREFIX} FETCH_ENTITY_PER_DAY_SUCCESS`;
export const FETCH_ENTITY_PER_DAY_FAILURE = `${PREFIX} FETCH_ENTITY_PER_DAY_FAILURE`;

export const fetchEntityPerDayRequest = (entityName, arrivalDate) => ({
  type: FETCH_ENTITY_PER_DAY_REQUEST,
  entityName,
  arrivalDate,
});

export const fetchEntityPerDaySuccess = (entityName, data, arrivalDate, isOverwrite = false) => {
  const normalizedData = normalize(data, entitiesStructureMap[entityName]);
  return {
    type: FETCH_ENTITY_PER_DAY_SUCCESS,
    entityName,
    arrivalDate,
    payload: {
      isOverwrite,
      ...normalizedData,
      result: {
        [entityName]: normalizedData.result,
      },
    },
  };
};

export const fetchEntityPerDayFailure = (entityName, arrivalDate) => ({
  type: FETCH_ENTITY_PER_DAY_FAILURE,
  entityName,
  arrivalDate,
});

export const fetchEntityPerDay =
  (entityName, { params: { arrivalDate }, url, isOptional = true }) =>
  (dispatch) => {
    dispatch(fetchEntityPerDayRequest(entityName, arrivalDate));
    return request.get(url, { params: { arrivalDate, fallback: 1 } }).then(
      ({ data }) => dispatch(fetchEntityPerDaySuccess(entityName, data, arrivalDate)),
      (error) => {
        dispatch(fetchEntityPerDayFailure(entityName, arrivalDate));
        if (!isOptional) {
          throw error;
        }
      }
    );
  };
